import React from 'react';
import { Target, Plus, Trash2, Edit2 } from 'lucide-react';
import { SavingsGoal } from '../lib/types';
import { cn, formatCurrency } from '../lib/utils';
import { motion } from 'motion/react';

interface SavingsTabProps {
  goals: SavingsGoal[];
  localCurrency: string;
  onManageGoals: () => void;
  onEditGoal: (goal: SavingsGoal) => void;
  onDeleteGoal: (id: string) => void;
  isPrivacyMode: boolean;
}

export function SavingsTab({ 
  goals, 
  localCurrency, 
  onManageGoals, 
  onEditGoal,
  onDeleteGoal,
  isPrivacyMode 
}: SavingsTabProps) {
  const totalTarget = goals.reduce((sum, goal) => sum + goal.targetAmount, 0);
  const totalCurrent = goals.reduce((sum, goal) => sum + goal.currentAmount, 0);
  const overallProgress = totalTarget > 0 ? (totalCurrent / totalTarget) * 100 : 0;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between px-1">
        <h2 className="text-xl font-bold text-zinc-900 dark:text-zinc-100">Savings Goals</h2>
        <button 
          onClick={onManageGoals}
          className="flex items-center gap-2 px-4 py-2 bg-zinc-900 dark:bg-zinc-100 text-white dark:text-zinc-900 rounded-xl text-xs font-bold uppercase tracking-wider shadow-sm hover:opacity-90 transition-opacity"
        >
          <Plus className="w-4 h-4" />
          Add Goal
        </button>
      </div>

      {/* Summary Card */}
      <div className="bg-zinc-900 dark:bg-zinc-800 text-white p-8 rounded-[32px] shadow-2xl shadow-zinc-200 dark:shadow-zinc-900/50 transition-colors">
        <div className="text-center space-y-2">
          <p className="text-zinc-400 text-[10px] font-bold uppercase tracking-widest">Total Savings Progress</p>
          <h2 className={cn("text-4xl font-bold tracking-tight text-emerald-400", isPrivacyMode && "blur-md select-none")}>
            {formatCurrency(totalCurrent, localCurrency)}
          </h2>
          <p className={cn("text-sm text-zinc-400", isPrivacyMode && "blur-sm select-none")}>
            saved of {formatCurrency(totalTarget, localCurrency)}
          </p>
          
          <div className="mt-6 space-y-2">
            <div className="flex justify-between text-[10px] font-bold uppercase tracking-wider text-zinc-500">
              <span>Overall Progress</span>
              <span>{overallProgress.toFixed(1)}%</span>
            </div>
            <div className="h-2 w-full bg-white/10 rounded-full overflow-hidden">
              <motion.div 
                initial={{ width: 0 }}
                animate={{ width: `${overallProgress}%` }}
                transition={{ duration: 1, ease: "easeOut" }}
                className="h-full bg-emerald-500"
              />
            </div>
          </div>
        </div>
      </div>

      {/* Goals List */}
      <div className="space-y-4">
        {goals.length === 0 ? (
          <div className="text-center py-12 text-zinc-400 text-sm italic">
            <Target className="w-8 h-8 mx-auto mb-2 opacity-50" />
            No savings goals yet. Add one to start tracking!
          </div>
        ) : (
          goals.map((goal) => {
            const percentage = Math.min((goal.currentAmount / goal.targetAmount) * 100, 100);
            return (
              <div key={goal.id} className="bg-white dark:bg-zinc-900 p-5 rounded-3xl border border-zinc-100 dark:border-zinc-800 shadow-sm">
                <div className="flex justify-between items-start mb-4">
                  <div className="flex-1">
                    <h4 className="font-bold text-zinc-900 dark:text-zinc-100">{goal.name}</h4>
                    <div className={cn("mt-1", isPrivacyMode && "blur-sm select-none")}>
                      <span className="text-lg font-bold text-emerald-600">{formatCurrency(goal.currentAmount, localCurrency)}</span>
                      <span className="text-xs text-zinc-400 font-medium ml-1">/ {formatCurrency(goal.targetAmount, localCurrency)}</span>
                    </div>
                  </div>
                  <div className="flex items-center gap-1">
                    <button 
                      onClick={() => onEditGoal(goal)}
                      className="p-2 text-zinc-400 hover:text-indigo-500 hover:bg-indigo-50 dark:hover:bg-indigo-900/20 rounded-xl transition-colors"
                    >
                      <Edit2 className="w-4 h-4" />
                    </button>
                    <button 
                      onClick={() => onDeleteGoal(goal.id)}
                      className="p-2 text-zinc-400 hover:text-rose-500 hover:bg-rose-50 dark:hover:bg-rose-900/20 rounded-xl transition-colors"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>

                <div className="h-2 w-full bg-zinc-100 dark:bg-zinc-800 rounded-full overflow-hidden">
                  <motion.div 
                    initial={{ width: 0 }}
                    animate={{ width: `${percentage}%` }}
                    transition={{ duration: 1, ease: "easeOut" }}
                    className="h-full rounded-full bg-emerald-500"
                  />
                </div>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
}